import { cn } from '@/utils/cn';
import CloseIcon from '@mui/icons-material/Close';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/core/keyboard/useFocusTrap';
import { ModalFormProvider } from '@/components/ui/ModalFormContext';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: ReactNode;
  /** When false, children render without the default modal form spacing wrapper. */
  formLayout?: boolean;
  /** Extra classes on the dialog panel (e.g. page-scoped typography). */
  panelClassName?: string;
}

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  footer,
  formLayout = true,
  panelClassName,
}: ModalProps) {
  const { t } = useTranslation('common');
  const trapRef = useFocusTrap(open);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} aria-hidden />
      <div
        ref={trapRef}
        className={cn(
          'relative z-10 flex max-h-[min(90vh,720px)] w-full flex-col rounded-xl border border-slate-200/80 bg-white text-slate-900 shadow-panel dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100',
          sizes[size],
          panelClassName,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0 pr-2">
            <h2 id="modal-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </h2>
            {description ? (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="icon-btn icon-btn-sm shrink-0"
            aria-label={t('labels.closeDialog')}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {formLayout ? (
            <ModalFormProvider value>
              <div className="modal-form">{children}</div>
            </ModalFormProvider>
          ) : (
            children
          )}
        </div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
